/**
 * Página de Comprovantes Pendentes (admin)
 * Lista comprovantes enviados pelos clientes via app. Admin pode aprovar
 * (registra pagamento real no sistema) ou rejeitar (com motivo).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiService } from '../../services/api';
import { useCompartilhamentos, CompartilhadaBadge } from './CompartilhadaBadge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Check, X, RefreshCw, FileText, Image as ImageIcon, ExternalLink, ArrowLeft, User, Calendar, Search, Trash2 } from 'lucide-react';

const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDataHora = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('pt-BR'); } catch { return d; }
};
const fmtData = (d) => {
  if (!d) return '—';
  try {
    // Aceita ISO ou YYYY-MM-DD
    const s = String(d);
    if (s.length === 10) return new Date(s + 'T00:00:00').toLocaleDateString('pt-BR');
    return new Date(s).toLocaleDateString('pt-BR');
  } catch { return d; }
};

// Pega a data do PAGAMENTO real (extraída pela análise IA) ou cai pra data de envio
const dataPagamentoOrdenacao = (d) => d?.analise_ia?.data_pagamento || d?.criado_em || '';

const ComprovantesClientePage = () => {
  const compartilhamentos = useCompartilhamentos();
  const usuarioLogado = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = usuarioLogado?.tipo === 'sub_admin';
  const [docs, setDocs] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('todos'); // pendente | aprovado | rejeitado | todos
  const [processando, setProcessando] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null); // null = vista por cliente; obj = vista detalhe
  const [busca, setBusca] = useState('');

  const sincronizarAcessos = async () => {
    if (!confirm('Criar acesso ao app (senha padrão: cliente123) para TODOS os clientes com locação ativa que ainda não têm acesso?\n\nClientes que já têm acesso não serão alterados.')) return;
    setSincronizando(true);
    try {
      const { data } = await apiService.api.post('/admin/cliente-app/sincronizar-todas-locacoes-ativas');
      alert(`✅ Sincronização concluída!\n\n• ${data.locacoes_ativas} locação(ões) ativa(s)\n• ${data.acessos_criados} novo(s) acesso(s) criado(s)\n• ${data.ja_existiam} já tinha(m) acesso\n• Senha padrão: ${data.senha_padrao}\n\nOs clientes precisarão trocar a senha no primeiro login.`);
    } catch (e) {
      alert('❌ Erro ao sincronizar: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSincronizando(false);
    }
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // Sempre buscamos TODOS — o agrupamento por cliente + filtro é feito no client-side.
      const { data } = await apiService.api.get('/admin/comprovantes-cliente/todos', { params: { limite: 1000 } });
      setDocs(data || []);
    } catch (e) {
      console.error('Erro ao listar comprovantes:', e);
      try {
        const { data } = await apiService.api.get('/admin/comprovantes-cliente/pendentes');
        setDocs(data || []);
      } catch {/* ignore */}
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const aprovar = async (id, statusAtual) => {
    const msg = statusAtual === 'rejeitado' || statusAtual === 'recusado_auto'
      ? 'Reverter rejeição e APROVAR este comprovante?\n\nO pagamento será registrado no sistema.'
      : 'Aprovar este comprovante? O pagamento será registrado no sistema.';
    if (!window.confirm(msg)) return;
    setProcessando(id);
    try {
      await apiService.api.post(`/admin/comprovantes-cliente/${id}/aprovar`);
      alert('✅ Comprovante aprovado e pagamento registrado!');
      carregar();
    } catch (e) {
      alert('Erro ao aprovar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProcessando(null);
    }
  };

  const rejeitar = async (id) => {
    const motivo = window.prompt('Qual o motivo da rejeição? (será mostrado ao cliente)', 'Valor não confere com o esperado');
    if (!motivo) return;
    setProcessando(id);
    try {
      await apiService.api.post(`/admin/comprovantes-cliente/${id}/rejeitar`, { motivo });
      alert('❌ Comprovante rejeitado. Cliente foi notificado.');
      carregar();
    } catch (e) {
      alert('Erro ao rejeitar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProcessando(null);
    }
  };

  const excluir = async (id, statusAtual) => {
    const aviso = statusAtual === 'aprovado'
      ? '⚠️ ATENÇÃO: este comprovante está APROVADO.\n\nA exclusão NÃO reverte automaticamente o pagamento/débito já registrado. Você precisará ajustar manualmente se desejar reverter.\n\nConfirma a exclusão?'
      : 'Excluir este comprovante? Esta ação não pode ser desfeita.';
    if (!window.confirm(aviso)) return;
    setProcessando(id);
    try {
      const { data } = await apiService.api.delete(`/admin/comprovantes-cliente/${id}`);
      alert(`🗑️ Comprovante excluído. ${data?.aviso ? '\n\n' + data.aviso : ''}`);
      carregar();
    } catch (e) {
      alert('Erro ao excluir: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProcessando(null);
    }
  };

  const abrirArquivo = (id) => {
    // Usa o key correto onde AuthContext salva ('authToken').
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/comprovantes-cliente/${id}/arquivo`;
    // Abre em nova aba carregando com o header de auth via fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, '_blank');
      })
      .catch((e) => alert('Erro ao abrir arquivo: ' + e.message));
  };

  // Server agora filtra; o filter-state também é usado pra contagem visual.
  // Para "recusado" agrupamos os 2 sub-status (rejeitado manual + recusado_auto).
  const docsFiltrados = (() => {
    if (filtro === 'recusado') return docs.filter((d) => d.status === 'rejeitado' || d.status === 'recusado_auto');
    if (filtro === 'todos') return docs;
    return docs.filter((d) => d.status === filtro);
  })();

  // Agrupa por cliente (cliente_id) para a vista "Por cliente".
  // Usa SEMPRE todos os docs (não filtrados) para os totais; o filtro de status
  // só afeta a vista de detalhe.
  const clientesAgrupados = useMemo(() => {
    const map = new Map();
    for (const d of docs) {
      const key = d.cliente_id || d.cliente_nome || '__sem_cliente__';
      if (!map.has(key)) {
        map.set(key, {
          cliente_id: d.cliente_id,
          cliente_nome: d.cliente_nome || 'Sem nome',
          total: 0,
          pendentes: 0,
          aprovados: 0,
          recusados: 0,
          total_valor: 0,
          ultimo_envio: null,
          ultima_data_pagamento: null,
          docs: [],
        });
      }
      const item = map.get(key);
      item.docs.push(d);
      item.total += 1;
      // Soma APENAS comprovantes aprovados — pendentes/rejeitados/recusados
      // não contam para o total recebido do cliente.
      if (d.status === 'aprovado') {
        item.total_valor += Number(d.valor || 0);
        item.aprovados += 1;
      } else if (d.status === 'pendente') {
        item.pendentes += 1;
      } else if (d.status === 'rejeitado' || d.status === 'recusado_auto') {
        item.recusados += 1;
      }
      const dt = d.criado_em || '';
      if (!item.ultimo_envio || dt > item.ultimo_envio) item.ultimo_envio = dt;
      const dp = d.analise_ia?.data_pagamento;
      if (dp && (!item.ultima_data_pagamento || dp > item.ultima_data_pagamento)) {
        item.ultima_data_pagamento = dp;
      }
    }
    // Ordena: clientes com pendentes primeiro, depois por último envio desc
    return Array.from(map.values()).sort((a, b) => {
      if (a.pendentes !== b.pendentes) return b.pendentes - a.pendentes;
      return (b.ultimo_envio || '').localeCompare(a.ultimo_envio || '');
    });
  }, [docs]);

  // Aplica busca textual sobre a lista agrupada
  const clientesVisiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return clientesAgrupados;
    return clientesAgrupados.filter((c) =>
      (c.cliente_nome || '').toLowerCase().includes(q)
    );
  }, [clientesAgrupados, busca]);

  // Quando um cliente está selecionado, filtra e ORDENA por data de pagamento (desc)
  const docsDoClienteSelecionado = useMemo(() => {
    if (!clienteSelecionado) return [];
    const filtroDocs =
      filtro === 'todos'
        ? clienteSelecionado.docs
        : filtro === 'recusado'
          ? clienteSelecionado.docs.filter((d) => d.status === 'rejeitado' || d.status === 'recusado_auto')
          : clienteSelecionado.docs.filter((d) => d.status === filtro);
    return [...filtroDocs].sort((a, b) =>
      dataPagamentoOrdenacao(b).localeCompare(dataPagamentoOrdenacao(a))
    );
  }, [clienteSelecionado, filtro]);

  // Re-sincroniza clienteSelecionado quando docs mudam (após aprovar/rejeitar)
  useEffect(() => {
    if (!clienteSelecionado) return;
    const atualizado = clientesAgrupados.find((c) => c.cliente_id === clienteSelecionado.cliente_id);
    if (atualizado) setClienteSelecionado(atualizado);
    else setClienteSelecionado(null); // cliente sumiu, volta pra lista
  }, [docs, clientesAgrupados]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="admin-comprovantes-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Comprovantes do App</h1>
          <p className="text-sm text-gray-400">Aprovar ou rejeitar pagamentos enviados pelos clientes via app.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={sincronizarAcessos} variant="outline" size="sm"
            className="border-emerald-700 text-emerald-300 hover:bg-emerald-900/30"
            disabled={sincronizando}
            data-testid="btn-sincronizar-acessos-app">
            📱 {sincronizando ? 'Sincronizando...' : 'Sincronizar acessos do app'}
          </Button>
          <Button onClick={carregar} variant="outline" size="sm" className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros por status */}
      <div className="flex flex-wrap gap-2" data-testid="admin-comprovantes-filtros">
        {[
          { key: 'pendente', label: 'Pendentes', color: 'amber' },
          { key: 'aprovado', label: 'Aprovados', color: 'emerald' },
          { key: 'recusado', label: 'Recusados', color: 'red' },
          { key: 'todos', label: 'Todos', color: 'blue' },
        ].map((o) => {
          const ativo = filtro === o.key;
          const total =
            o.key === 'todos'
              ? docs.length
              : o.key === 'recusado'
                ? docs.filter((d) => d.status === 'rejeitado' || d.status === 'recusado_auto').length
                : docs.filter((d) => d.status === o.key).length;
          const colors = {
            amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-300' },
            emerald: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', text: 'text-emerald-300' },
            red: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-300' },
            blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-300' },
          }[o.color];
          return (
            <button
              key={o.key}
              onClick={() => setFiltro(o.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                ativo
                  ? `${colors.bg} ${colors.border} ${colors.text}`
                  : 'bg-slate-800 text-gray-400 border-slate-700 hover:bg-slate-700'
              }`}
              data-testid={`admin-filtro-comprovante-${o.key}`}
            >
              {o.label} ({total})
            </button>
          );
        })}
      </div>

      {/* Busca por nome de cliente — só na vista de grade */}
      {!clienteSelecionado && (
        <div className="relative max-w-md" data-testid="admin-busca-container">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar cliente por nome..."
            className="w-full pl-9 pr-9 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30"
            data-testid="admin-busca-input"
          />
          {busca && (
            <button
              onClick={() => setBusca('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              data-testid="admin-busca-limpar"
              aria-label="Limpar busca"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Aviso resumo */}
      <Card className="bg-blue-500/10 border-blue-500/30 p-4">
        <div className="text-sm text-blue-200">
          {clienteSelecionado ? (
            <>
              Histórico de <strong>{clienteSelecionado.cliente_nome}</strong> ({clienteSelecionado.total} comprovantes
              · {fmt(clienteSelecionado.total_valor)} aprovado). Ordenado por <strong>data do pagamento</strong>.
            </>
          ) : (
            <>
              <strong>{busca ? clientesVisiveis.length : clientesAgrupados.length}</strong>
              {busca ? ` de ${clientesAgrupados.length}` : ''} cliente(s) com comprovantes. Toque em um cliente para ver seu histórico organizado por data do pagamento.
            </>
          )}
        </div>
      </Card>

      {/* Botão voltar quando em vista detalhe */}
      {clienteSelecionado && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setClienteSelecionado(null)}
          className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
          data-testid="btn-voltar-clientes"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para clientes
        </Button>
      )}

      {carregando ? (
        <div className="text-center py-10 text-gray-400">Carregando...</div>
      ) : !clienteSelecionado ? (
        /* ====== VISTA POR CLIENTE — grade de cards ====== */
        clientesAgrupados.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 p-8 text-center">
            <Check className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg text-white font-semibold">Sem comprovantes</h3>
            <p className="text-sm text-gray-400 mt-1">Nenhum cliente enviou comprovantes ainda.</p>
          </Card>
        ) : clientesVisiveis.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 p-8 text-center" data-testid="admin-busca-vazia">
            <Search className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <h3 className="text-lg text-white font-semibold">Nenhum cliente encontrado</h3>
            <p className="text-sm text-gray-400 mt-1">Tente outro nome ou limpe a busca.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-grid-clientes">
            {clientesVisiveis.map((cli) => (
              <button
                key={cli.cliente_id || cli.cliente_nome}
                onClick={() => setClienteSelecionado(cli)}
                className="text-left transition-all hover:scale-[1.01] hover:border-emerald-500/40"
                data-testid={`cliente-card-${cli.cliente_id || cli.cliente_nome}`}
              >
                <Card className={`p-4 space-y-3 border ${
                  cli.pendentes > 0
                    ? 'bg-amber-500/5 border-amber-500/30'
                    : 'bg-slate-900 border-slate-800'
                }`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-emerald-300" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-semibold truncate flex items-center gap-1.5 flex-wrap">
                          {cli.cliente_nome}
                          <CompartilhadaBadge nomes={compartilhamentos.por_cliente[cli.cliente_id]} />
                        </div>
                        <div className="text-xs text-gray-400">{cli.total} comprovante{cli.total > 1 ? 's' : ''}</div>
                      </div>
                    </div>
                    {cli.pendentes > 0 && (
                      <Badge className="bg-amber-500/25 text-amber-200 border border-amber-500/50 flex-shrink-0">
                        {cli.pendentes} pendente{cli.pendentes > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-800/50 rounded p-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Pendentes</div>
                      <div className="text-amber-400 font-bold text-lg">{cli.pendentes}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded p-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Aprovados</div>
                      <div className="text-emerald-400 font-bold text-lg">{cli.aprovados}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded p-2">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Recusados</div>
                      <div className="text-red-400 font-bold text-lg">{cli.recusados}</div>
                    </div>
                  </div>

                  <div className="space-y-1 text-xs text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      Último envio: {fmtDataHora(cli.ultimo_envio)}
                    </div>
                    {cli.ultima_data_pagamento && (
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        Último pagamento: {fmtData(cli.ultima_data_pagamento)}
                      </div>
                    )}
                    <div className="text-emerald-300 font-semibold" data-testid="cliente-total-aprovado">
                      Total aprovado: {fmt(cli.total_valor)}
                    </div>
                  </div>
                </Card>
              </button>
            ))}
          </div>
        )
      ) : docsDoClienteSelecionado.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 p-8 text-center">
          <h3 className="text-lg text-white font-semibold">Sem comprovantes neste filtro</h3>
          <p className="text-sm text-gray-400 mt-1">
            Mude o filtro acima para ver os outros comprovantes de {clienteSelecionado.cliente_nome}.
          </p>
        </Card>
      ) : (
        /* ====== VISTA DETALHE DO CLIENTE — ordenada por data do pagamento ====== */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-comprovantes-detalhe">
          {docsDoClienteSelecionado.map((d) => (
            <Card key={d.id} className="bg-slate-900 border-slate-800 p-4 space-y-3" data-testid={`comprovante-card-${d.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold">{d.cliente_nome || '—'}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    Pagamento: <strong className="text-gray-300">{fmtData(d.analise_ia?.data_pagamento) !== '—' ? fmtData(d.analise_ia?.data_pagamento) : 'data não detectada'}</strong>
                    {d.analise_ia?.horario && <span className="ml-1">{d.analise_ia.horario}</span>}
                  </div>
                  <div className="text-xs text-gray-500">Enviado em {fmtDataHora(d.criado_em)}</div>
                </div>
                <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  {fmt(d.valor)}
                </Badge>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-300 flex-wrap">
                <Badge variant="outline" className="border-slate-700">
                  {d.tipo === 'aluguel'
                    ? '🏠 Aluguel'
                    : d.parcela_numero
                      ? `💳 Débito · Parcela ${d.parcela_numero}/${d.parcelas_total}${d.valor_parcela ? ` · ${fmt(d.valor_parcela)}` : ''}`
                      : '💳 Débito'}
                </Badge>
                {(d.arquivo_content_type || '').startsWith('image/') ? (
                  <span className="flex items-center gap-1 text-gray-400"><ImageIcon className="w-3 h-3" /> Imagem</span>
                ) : (
                  <span className="flex items-center gap-1 text-gray-400"><FileText className="w-3 h-3" /> PDF</span>
                )}
                {/* Badge de status */}
                {d.status === 'pendente' && (
                  <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40">Pendente</Badge>
                )}
                {d.status === 'aprovado' && (
                  <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">✓ Aprovado</Badge>
                )}
                {d.status === 'rejeitado' && (
                  <Badge className="bg-red-500/20 text-red-300 border border-red-500/40">✗ Rejeitado</Badge>
                )}
                {d.status === 'recusado_auto' && (
                  <Badge className="bg-red-500/20 text-red-300 border border-red-500/40">🤖 Recusado auto</Badge>
                )}
              </div>

              {d.motivo_recusa_auto && (
                <div className="text-xs text-red-300 bg-red-500/5 border border-red-500/30 rounded p-2" data-testid={`motivo-recusa-auto-${d.id}`}>
                  <strong>Motivo (auto):</strong> {d.motivo_recusa_auto}
                </div>
              )}
              {d.motivo_rejeicao && (
                <div className="text-xs text-red-300 bg-red-500/5 border border-red-500/30 rounded p-2">
                  <strong>Motivo (admin):</strong> {d.motivo_rejeicao}
                </div>
              )}

              {d.observacoes && (
                <div className="text-xs text-gray-400 italic bg-slate-800/50 rounded p-2">
                  &ldquo;{d.observacoes}&rdquo;
                </div>
              )}

              {d.analise_ia && (() => {
                const ai = d.analise_ia;
                const dup = !!ai.suspeita_duplicata;
                const okValor = ai.valor_confere;
                const okDest = ai.destinatario_confere;
                const conf = ai.confianca;
                return (
                  <div
                    className={`text-xs rounded-lg p-3 space-y-2 border ${
                      dup
                        ? 'bg-orange-500/10 border-orange-500/40'
                        : okValor && okDest !== false
                          ? 'bg-emerald-500/5 border-emerald-500/30'
                          : 'bg-slate-800/50 border-slate-700'
                    }`}
                    data-testid={`analise-ia-${d.id}`}
                  >
                    <div className="flex items-center gap-2 font-semibold text-gray-200">
                      <span>🤖 Análise automática</span>
                      {dup && (
                        <Badge className="bg-orange-500/30 text-orange-200 border border-orange-500/50">
                          🟠 SUSPEITA DUPLICATA
                        </Badge>
                      )}
                      {!dup && okValor && okDest !== false && (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          ✓ Conferido
                        </Badge>
                      )}
                      {conf && (
                        <span className="ml-auto text-[10px] uppercase tracking-wider text-gray-500">
                          confiança {conf}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-300">
                      <div><span className="text-gray-500">Valor extraído:</span> <span className={okValor ? 'text-emerald-300' : 'text-red-300'}>{ai.valor != null ? `R$ ${Number(ai.valor).toFixed(2)}` : '—'}</span></div>
                      <div><span className="text-gray-500">Data:</span> {ai.data_pagamento || '—'} {ai.horario && <span className="text-gray-500">{ai.horario}</span>}</div>
                      <div className="col-span-2"><span className="text-gray-500">TxID/E2E:</span> <span className="font-mono text-[10px] break-all">{ai.txid_e2e || '—'}</span></div>
                      <div><span className="text-gray-500">Pagador:</span> {ai.pagador_nome || '—'}</div>
                      <div><span className="text-gray-500">CPF:</span> {ai.pagador_cpf_cnpj || '—'}</div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Destinatário:</span>{' '}
                        <span className={okDest === false ? 'text-red-300' : 'text-gray-200'}>
                          {ai.destinatario_nome || '—'} ({ai.destinatario_cpf_cnpj || '—'})
                        </span>
                      </div>
                    </div>
                    {dup && ai.duplicata_info?.comprovantes_anteriores?.length > 0 && (
                      <div className="border-t border-orange-500/30 pt-2 mt-1">
                        <div className="text-orange-200 font-medium">
                          {ai.duplicata_info.tipo === 'txid_identico'
                            ? '⚠ Mesmo TxID enviado anteriormente:'
                            : '⚠ Valor + data + pagador iguais a:'}
                        </div>
                        <ul className="text-[11px] text-gray-300 list-disc ml-4 mt-1">
                          {ai.duplicata_info.comprovantes_anteriores.slice(0, 3).map((c, i) => (
                            <li key={i}>
                              {c.cliente_nome} · R$ {Number(c.valor).toFixed(2)} · {c.criado_em?.slice(0, 10)} · status: {c.status}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button
                  onClick={() => abrirArquivo(d.id)}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-slate-700 text-slate-200"
                  data-testid={`btn-ver-comprovante-${d.id}`}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver comprovante
                </Button>
                {/* Aprovar: aparece para pendentes E rejeitados/recusados (admin pode reverter) */}
                {!isSubAdmin && (d.status === 'pendente' || d.status === 'recusado_auto' || d.status === 'rejeitado') && (
                  <Button
                    onClick={() => aprovar(d.id, d.status)}
                    disabled={processando === d.id}
                    size="sm"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`btn-aprovar-comprovante-${d.id}`}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {d.status === 'pendente' ? 'Aprovar' : 'Reverter e aprovar'}
                  </Button>
                )}
                {/* Rejeitar: apenas para pendentes / recusado_auto */}
                {!isSubAdmin && (d.status === 'pendente' || d.status === 'recusado_auto') && (
                  <Button
                    onClick={() => rejeitar(d.id)}
                    disabled={processando === d.id}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-red-500/30 text-red-300 hover:bg-red-500/10"
                    data-testid={`btn-rejeitar-comprovante-${d.id}`}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Rejeitar
                  </Button>
                )}
                {/* Excluir: disponível em QUALQUER status */}
                {!isSubAdmin && (
                <Button
                  onClick={() => excluir(d.id, d.status)}
                  disabled={processando === d.id}
                  variant="outline"
                  size="sm"
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                  data-testid={`btn-excluir-comprovante-${d.id}`}
                  title="Excluir comprovante"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComprovantesClientePage;
